import type {BasedOnItem} from '../../../nova-invocation/BasedOnList';
import {failureCauseFromRecord, type RunOutcome} from '../../../nova-invocation/novaInvocationStore';

// BasedOnList, FailurePresentation and RoutingDisclosure were built against shapes the saved
// record does not have. This module is the only place that reconciles the two, so those
// components stay exactly as delivered.

interface ResolvedEntry {
	token: string | null;
	scope: string | null;
}

interface Grounding {
	resolved?: ReadonlyArray<ResolvedEntry> | null;
}

// Who is sending a reply, for the turn that stands in for a record nothing has read back yet.
type ReplyAuthor = {fullName: string | null; profilePictureId: string | null} | null;

// {task_title} -> "Task title". The token is the template's own variable name, which is a handle:
// a customer surface shows what was read, not the spelling the definition used to ask for it.
function readableToken(token: string | null): string {
	if (!token) return '';
	const bare = token.replace(/^\{/, '').replace(/\}$/, '').replace(/_/g, ' ').trim();
	if (!bare) return '';
	return bare.charAt(0).toUpperCase() + bare.slice(1);
}

// What the run actually read: the sources whose values were substituted into the prompt the model
// saw (the record's `resolved` list is built from exactly those substitutions). The account's other
// lists are deliberately left out.
//
// A `skipped` reference is one the definition named that resolved to nothing - a task with no
// description, a project with no client - so nothing about it reached the model, and listing it
// under "Based on" would name a source the answer was not based on. `withheld` is absent for a
// different reason: withholding is enforced, and this release does not disclose it.
export function basedOnItemsFor(grounding: Grounding | null): BasedOnItem[] {
	if (!grounding) return [];
	// One row per source, not per mention: a template naming {task_title} twice read the title
	// once. The record already deduplicates; this keeps that true of anything that does not.
	const seen = new Set<string>();
	const items: BasedOnItem[] = [];
	for (const entry of grounding.resolved ?? []) {
		const name = readableToken(entry.token);
		if (!name || seen.has(name)) continue;
		seen.add(name);
		items.push({variableName: name});
	}
	return items;
}

export function outcomeFor(run: {outcome: string | null; failureState: string | null}): RunOutcome {
	if (run.outcome === 'SUCCEEDED') return {kind: 'succeeded'};
	if (run.outcome === 'CANCELLED') return {kind: 'cancelled'};
	if (run.outcome === 'FAILED') return {kind: 'failed', cause: failureCauseFromRecord(run.failureState)};
	// Neither terminal outcome, and not cancelled. Reporting success on a shape nothing confirmed is
	// the one answer that would mislead, so an unknown outcome reads as a generic failure - the same
	// posture novaInvocationStore's commitRun takes for the same situation.
	return {kind: 'failed', cause: 'generic'};
}

// A run with no saved answer is not a turn with an empty body: it is a turn that has no body yet
// (still running) or never got one (failed). Either way the view shows its state, not blank text.
export function isAnswered(run: {answer: string | null}): boolean {
	return !!run.answer;
}

// What a person actually typed into the reply box, out of the text a run saved.
//
// A run records the user-role content it sent to the model, and for a reply that is the opening
// run's whole assembled prompt with the follow-up appended to it - so the saved text of a reply is
// the opening run's text, then the typed message. Showing it whole puts the agent's instructions,
// the client, the project and the task on screen as though the reader had written them.
//
// Stripped by prefix rather than by pattern: the opening run's own saved text IS the prefix, so
// this is an exact comparison against a known value, not a guess at what a prompt looks like. A
// reply that does not start with it is returned untouched - better to show more than the person
// wrote than to cut into something this cannot account for.
export function replyTextFor(userMessageText: string | null, openingUserMessageText: string | null): string | null {
	if (userMessageText === null) return null;
	if (openingUserMessageText === null || !userMessageText.startsWith(openingUserMessageText)) {
		return userMessageText;
	}
	const typed = userMessageText.slice(openingUserMessageText.length).trim();
	// An empty remainder means this run saved the prompt and nothing else, so there is no reply here.
	return typed.length > 0 ? typed : null;
}

// What it takes to run a failed turn again, or null when nothing would be run.
//
// Two shapes, decided by where the turn sits in its conversation rather than by how it failed:
//
//  - the opening turn IS the conversation, so running it again starts a new one. invoke() spells
//    that as a call carrying neither a message nor a thread id, which lets the server mint a fresh
//    thread (see invoke's own note on the pair). The agent is the one the turn already named, which
//    is the agent the person selected.
//  - every later turn is a reply, so running it again sends that same reply on the thread it was
//    refused from - the most recent failed exchange, resumed, not restarted.
//
// Null in three cases, each a reason to withhold the control rather than to send something
// approximate: a run that saved no agent id has nothing to invoke; and a reply missing either its
// text or its thread id has nothing to resend - sending it with the other half alone would reach
// invoke() as an unpaired call, i.e. as an opening run, silently replacing the conversation the
// person was trying to continue.
export function retryArgsFor(args: {
	isOpeningTurn: boolean;
	agentId: string | null;
	threadId: string | null;
	replyText: string | null;
	replyAuthor?: {fullName: string | null; profilePictureId: string | null} | null;
}): {agentId: string; opts?: {message: string; threadId: string; replyAuthor: ReplyAuthor}} | null {
	if (!args.agentId) return null;
	if (args.isOpeningTurn) return {agentId: args.agentId};
	if (!args.replyText || !args.threadId) return null;
	return {
		agentId: args.agentId,
		opts: {message: args.replyText, threadId: args.threadId, replyAuthor: args.replyAuthor ?? null},
	};
}
